import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Phone, Facebook, Instagram, Linkedin, Youtube, MapPin } from 'lucide-react';
import { companyInfo } from '../../data/mock';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const [navSettings, setNavSettings] = useState({ show_standards: true, show_plans: true });

  // Fetch navigation settings
  useEffect(() => {
    const fetchNavSettings = async () => {
      try {
        const res = await fetch(`${API_URL}/api/site-settings/navigation`);
        if (res.ok) {
          const data = await res.json();
          setNavSettings(data);
        }
      } catch (error) {
        console.error('Error fetching nav settings:', error);
      }
    };
    fetchNavSettings();
  }, []);

  // Build quick links based on visibility settings
  const quickLinks = [
    { name: 'Home', path: '/' },
    { name: 'Services', path: '/services' },
    ...(navSettings.show_plans !== false ? [{ name: 'Plans', path: '/plans' }] : []),
    { name: 'Portfolio', path: '/gallery' },
    { name: 'About Us', path: '/about' },
    ...(navSettings.show_standards !== false ? [{ name: 'Standards', path: '/standards' }] : []),
    { name: 'FAQ', path: '/faq' },
    { name: 'JobTread', path: '/jobtread' },
    { name: 'Contact', path: '/contact' },
  ];

  const services = [
    { name: 'New Builds', path: '/services' },
    { name: 'Home Transformations', path: '/services' },
    { name: 'Additions', path: '/services' },
    { name: 'Remodeling', path: '/services' },
  ];

  const resources = [
    { name: 'Customer Portal Support', url: 'https://www.jobtread.com/resources/help/customer-portal', external: true, category: 'Customers' },
    { name: 'Vendor Application', path: '/vendor-application', category: 'Vendors' },
    { name: 'Vendor Portal Support', url: 'https://www.jobtread.com/resources/help/vendor-portal', external: true, category: 'Vendors' },
    { name: 'Join Our Team', path: '/careers', category: 'Careers' },
    { name: 'Employment Application', path: '/employment-application', category: 'Careers' },
  ];

  const serviceAreas = [
    'Charlotte, NC',
    'Concord, NC',
    'Kannapolis, NC',
    'Davidson, NC',
    'Cornelius, NC',
    'Mooresville, NC',
    'Lake Norman, NC',
    'China Grove, NC',
  ];

  return (
    <footer className="bg-slate-900 text-white">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-10">
          {/* Company Info - spans 2 columns */}
          <div className="lg:col-span-2">
            <Link to="/" className="inline-block mb-6">
              <img
                src={companyInfo.logo}
                alt="C3 Contracting"
                className="h-28 w-auto"
              />
            </Link>
            <p className="text-slate-400 text-sm leading-relaxed mb-6">
              Building with a deeper purpose. We bring your vision to life with innovative design, expert craftsmanship, and seamless project execution throughout the Charlotte metro area.
            </p>
            <div className="flex gap-3">
              <a
                href="https://www.facebook.com/C3ContractingLLC"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center hover:bg-cyan-600 transition-colors"
                aria-label="Facebook"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a
                href="https://www.instagram.com/c3.contracting/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center hover:bg-cyan-600 transition-colors"
                aria-label="Instagram"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a
                href="https://www.linkedin.com/company/c3contracting"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center hover:bg-cyan-600 transition-colors"
                aria-label="LinkedIn"
              >
                <Linkedin className="w-5 h-5" />
              </a>
              <a
                href="https://www.youtube.com/@C3ContractingLLC"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center hover:bg-cyan-600 transition-colors"
                aria-label="YouTube"
              >
                <Youtube className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-semibold mb-5 text-white">Quick Links</h3>
            <ul className="space-y-2.5">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.path}
                    className="text-slate-400 hover:text-cyan-500 transition-colors text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-lg font-semibold mb-5 text-white">Our Services</h3>
            <ul className="space-y-2.5">
              {services.map((service) => (
                <li key={service.name}>
                  <Link
                    to={service.path}
                    className="text-slate-400 hover:text-cyan-500 transition-colors text-sm"
                  >
                    {service.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Resources */}
          <div>
            <h3 className="text-lg font-semibold mb-5 text-white">Resources</h3>
            <ul className="space-y-2.5">
              {resources.map((resource) => (
                <li key={resource.name}>
                  {resource.external ? (
                    <a
                      href={resource.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-slate-400 hover:text-cyan-500 transition-colors text-sm"
                    >
                      {resource.name}
                    </a>
                  ) : (
                    <Link
                      to={resource.path}
                      className="text-slate-400 hover:text-cyan-500 transition-colors text-sm"
                    >
                      {resource.name}
                    </Link>
                  )}
                </li>
              ))}
              <li>
                <a
                  href={companyInfo.jobtreadLoginUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-slate-400 hover:text-cyan-500 transition-colors text-sm"
                >
                  Customer / Vendor Login
                </a>
              </li>
              <li>
                <Link
                  to="/admin/login"
                  className="text-slate-400 hover:text-cyan-500 transition-colors text-sm"
                >
                  Admin Login
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-lg font-semibold mb-5 text-white">Contact Us</h3>
            <ul className="space-y-3">
              <li className="flex items-center gap-3">
                <Phone className="w-4 h-4 text-cyan-500 flex-shrink-0" />
                <a
                  href={`tel:${companyInfo.phone}`}
                  className="text-slate-400 hover:text-cyan-500 transition-colors text-sm"
                >
                  {companyInfo.phone}
                </a>
              </li>
              <li>
                <Link
                  to="/contact"
                  className="text-slate-400 hover:text-cyan-500 transition-colors text-sm"
                >
                  Contact Form
                </Link>
              </li>
            </ul>
          </div>
        </div>

        {/* Service Areas Section */}
        <div className="mt-12 pt-10 border-t border-slate-800">
          <div className="flex items-center gap-2 mb-4">
            <MapPin className="w-5 h-5 text-cyan-500" />
            <h3 className="text-lg font-semibold text-white">Service Areas</h3>
          </div>
          <p className="text-slate-400 text-sm mb-4">
            Proudly serving residential construction clients throughout the Charlotte metro and Lake Norman region:
          </p>
          <div className="flex flex-wrap gap-x-6 gap-y-2">
            {serviceAreas.map((area, index) => (
              <span key={area} className="text-slate-400 text-sm">
                {area}{index < serviceAreas.length - 1 && <span className="text-slate-600 ml-6">•</span>}
              </span>
            ))}
          </div>
        </div>

        {/* Industry Associations */}
        <div className="mt-12 pt-10 border-t border-slate-800">
          <p className="text-slate-500 text-sm uppercase tracking-wider mb-6 text-center">Proud Member Of</p>
          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-16">
            <a 
              href="https://www.nahb.org" 
              target="_blank" 
              rel="noopener noreferrer"
              className="opacity-80 hover:opacity-100 transition-opacity"
            >
              <img 
                src="/images/nahb-logo.png" 
                alt="National Association of Home Builders" 
                className="h-24 w-auto"
              />
            </a>
            <a 
              href="https://www.hbagc.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="opacity-90 hover:opacity-100 transition-opacity"
            >
              <img 
                src="/images/hbagc-logo.jpg" 
                alt="Home Builders Association of Greater Charlotte" 
                className="h-28 w-auto rounded"
              />
            </a>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-slate-500 text-sm">
              © {currentYear} C3 Contracting, LLC. All rights reserved.
            </p>
            <div className="flex flex-wrap justify-center gap-4 md:gap-6">
              <Link to="/privacy-policy" className="text-slate-500 hover:text-cyan-500 text-sm transition-colors">
                Privacy Policy
              </Link>
              <Link to="/terms-of-service" className="text-slate-500 hover:text-cyan-500 text-sm transition-colors">
                Terms of Service
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
